import { useRef, type TouchEvent } from 'react';

/** Протянул вправо дальше — отпустил — ответ, как в WhatsApp. */
const TRIGGER_PX = 56;
/** Дальше пузырь тянется туго, с сопротивлением. */
const MAX_PX = 72;
/** Пока палец сдвинулся меньше — не решаем, свайп это или прокрутка. */
const SLOP_PX = 8;

const SETTLE = '220ms cubic-bezier(0.2, 0.8, 0.2, 1)';

interface Gesture {
  x: number;
  y: number;
  mode: 'pending' | 'swipe' | 'scroll';
  dx: number;
}

/**
 * Свайп сообщения вправо → ответ. Двигаем строку через style, без setState:
 * лента не перерисовывается на каждый кадр. Прокрутку по вертикали отдаёт
 * браузеру `touch-action: pan-y` на строке.
 */
export function useSwipeReply(onReply: (() => void) | undefined) {
  const rowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const gesture = useRef<Gesture | null>(null);

  const paint = (dx: number, settle: boolean) => {
    const row = rowRef.current;
    const icon = iconRef.current;
    if (row) {
      row.style.transition = settle ? `transform ${SETTLE}` : 'none';
      row.style.transform = dx > 0 ? `translate3d(${dx}px, 0, 0)` : '';
    }
    if (icon) {
      const p = Math.min(1, dx / TRIGGER_PX);
      icon.style.transition = settle ? `opacity ${SETTLE}, transform ${SETTLE}` : 'none';
      icon.style.opacity = String(p);
      icon.style.transform = `translateY(-50%) scale(${0.5 + 0.5 * p})`;
    }
  };

  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    gesture.current = onReply && t && e.touches.length === 1 ? { x: t.clientX, y: t.clientY, mode: 'pending', dx: 0 } : null;
  };

  const onTouchMove = (e: TouchEvent) => {
    const g = gesture.current;
    const t = e.touches[0];
    if (!g || !t || g.mode === 'scroll') return;
    const dx = t.clientX - g.x;
    const dy = t.clientY - g.y;
    if (g.mode === 'pending') {
      if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
      g.mode = dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5 ? 'swipe' : 'scroll';
      if (g.mode === 'scroll') return;
    }
    const pulled = Math.max(0, dx);
    const d = pulled <= MAX_PX ? pulled : MAX_PX + (pulled - MAX_PX) * 0.15;
    // Щелчок вибрацией, когда дотянул до ответа (Android; на iOS API нет).
    if (d >= TRIGGER_PX && g.dx < TRIGGER_PX) navigator.vibrate?.(8);
    g.dx = d;
    paint(d, false);
  };

  const finish = (commit: boolean) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.mode !== 'swipe') return;
    paint(0, true);
    if (commit && g.dx >= TRIGGER_PX) onReply?.();
  };

  return {
    rowRef,
    iconRef,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: () => finish(true),
      onTouchCancel: () => finish(false),
    },
  };
}
